import { useCallback, useEffect, useState } from "react"

export type DropdownPlacement = {
  placement: "top" | "bottom"
  left: number
  width: number
  /** Definido quando placement === "bottom" (abre para baixo, ancorado no topo). */
  top?: number
  /** Definido quando placement === "top" (abre para cima, ancorado na base). */
  bottom?: number
  maxHeight: number
}

const VIEWPORT_MARGIN = 8

/**
 * Posiciona um dropdown "portalizado" (renderizado fora do fluxo normal via
 * createPortal) ancorado no seu elemento gatilho, escolhendo a direção
 * (cima/baixo) com mais espaço disponível — o mesmo critério de colisão que o
 * Radix usa internamente nos componentes prontos (Select, Popover).
 *
 * Existiam múltiplas implementações manuais desse "abre pra baixo com altura
 * fixa em px" espalhadas pelo código, cada uma decidindo a direção e o limite
 * de altura por conta própria — o que fazia a lista estourar a tela sempre
 * que o gatilho estivesse perto da borda. Centralizado aqui para não
 * precisar resolver esse mesmo problema de novo em cada lugar.
 *
 * `maxHeight` retornado é para a CAIXA INTEIRA do dropdown (incluindo barra de
 * busca ou rodapé, se houver) — quem consome deve montar esse conteúdo como
 * uma coluna flex com a lista de opções em `flex-1 min-h-0 overflow-y-auto`,
 * em vez de tentar subtrair a altura de elementos fixos manualmente.
 */
export function useDropdownPlacement(
  open: boolean,
  triggerRef: React.RefObject<HTMLElement | null>,
  options: { maxHeight?: number; minHeight?: number } = {}
): DropdownPlacement | null {
  const { maxHeight = 300, minHeight = 60 } = options
  const [placement, setPlacement] = useState<DropdownPlacement | null>(null)

  const update = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN
    const spaceAbove = rect.top - VIEWPORT_MARGIN

    // Abre para baixo por padrão; só inverte quando o espaço de baixo não é
    // suficiente para a altura que esse dropdown pediu (maxHeight) E há mais
    // espaço em cima do que embaixo — ou seja, "não coube embaixo".
    const openAbove = spaceBelow < maxHeight && spaceAbove > spaceBelow

    const available = Math.max(openAbove ? spaceAbove : spaceBelow, minHeight)

    setPlacement({
      placement: openAbove ? "top" : "bottom",
      left: rect.left,
      width: rect.width,
      top: openAbove ? undefined : rect.bottom + 4,
      bottom: openAbove ? window.innerHeight - rect.top + 4 : undefined,
      maxHeight: Math.min(maxHeight, available),
    })
  }, [triggerRef, maxHeight, minHeight])

  useEffect(() => {
    if (!open) return
    update()
    const handle = () => update()
    window.addEventListener("resize", handle)
    window.addEventListener("scroll", handle, true)
    return () => {
      window.removeEventListener("resize", handle)
      window.removeEventListener("scroll", handle, true)
    }
  }, [open, update])

  return open ? placement : null
}
